<template>
  <div class="container">
    <o-bread-crumb>
      <template v-slot:pageName>Voucher</template>
      <template v-slot:pageDetail>Gift History</template>
    </o-bread-crumb>

    <div v-if="validation" class="alert alert-danger mt-1" role="alert">{{ validation }}</div>

    <FeatureGate featureKey="voucher_gift">
    <div v-if="thisComponentShow" class="card text-center bg-white shadow-none">
      <div class="card-header">
        <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
          <li class="nav-item">
            <div class="header-content-left text-start">
              <h1 class="header-title">Gift History</h1>
              <p class="header-subtitle">Gift voucher distribution history</p>
            </div>
          </li>
        </ul>
      </div>

      <div class="card-body">
        <div class="spinner d-flex justify-content-center align-items-center mb-4 mt-4" v-if='page.isLoading'>
          <div class="spinner-border text-primary" role="status"></div>
        </div>

        <div v-if="page.output">
          <div class="filter-action-wrapper mb-4 mt-3">
            <div class="filter-controls">
              <!-- Search Box -->
              <div class="search-box-custom">
                <div class="search-type-tabs">
                  <button 
                    v-for="opt in searchOptions" 
                    :key="opt.value"
                    :class="['tab-btn', { active: searchSelected === opt.value }]"
                    @click="searchSelected = opt.value"
                  >
                    {{ opt.text }}
                  </button>
                </div>
                <div class="search-input-wrapper">
                  <input 
                    type="text" 
                    class="form-control-search" 
                    v-model="searchCriteria" 
                    @keyup.enter="doSearch"
                    :placeholder="'Search by ' + (searchOptions.find(o => o.value === searchSelected)?.text || '...') " 
                  />
                  <button v-if="!isSearchActive" class="btn-search" @click="doSearch">
                    <i class="fas fa-search"></i>
                  </button>
                  <button v-else class="btn-search" @click="clearSearch">
                    <i class="fas fa-times text-danger"></i>
                  </button>
                </div>
              </div>

              <!-- Status Filter -->
              <div class="filter-section">
                <div class="section-label-tabs">
                  <span class="label-tab">Status</span>
                </div>
                <div class="date-range-box">
                  <div class="status-filter-box">
                    <select class="form-select form-control-filter" v-model="filterStatus" @change="applyFilter">
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="processing">Processing</option>
                      <option value="done">Done</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div class="action-section">
              <div class="action-buttons-group">
                <button class="btn-refresh" @click="loadState">
                  <i class="fas fa-sync-alt"></i>
                </button>
                <router-link :to="{ name: 'VoucherGift' }" class="btn-add">
                  <i class="fas fa-plus me-1"></i> New Gift
                </router-link>
              </div>
            </div>
          </div>

          <o-table style="text-align: left" class="table-hover member-list-table">
            <thead>
              <tr>
                <td class="fw-bolder text-muted" v-for="(f, i) in fields" :key="i">{{ f }}</td>
                <td class="fw-bolder text-muted">Action</td>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in distributions" :key="index" class="member-row-clickable">
                <td>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                <td>
                  <span class="voucher-title" :title="item.voucher_name">
                    {{ item.voucher_name.length > 25 ? item.voucher_name.substring(0, 25) + '..' : item.voucher_name }}
                  </span>
                </td>
                <td>{{ formatTarget(item) }}</td>
                <td>{{ item.total_sent }}</td>
                <td>
                  <span class="status-badge" :class="statusBadge(item.status)">{{ item.status }}</span>
                </td>
                <td>{{ item.distributed_by_name || '-' }}</td>
                <td><div class="note-cell">{{ item.note || '-' }}</div></td>
                <td><span class="date-text">{{ formatDate(item.created_at) }}</span></td>
                <td>
                  <router-link v-if="item.status === 'done'" class="btn btn-action btn-info-action" :to="{ name: 'ShowVoucherGiftHistory', params: { id: item.id } }">
                    <i class="fas fa-eye me-1"></i> View Recipients
                  </router-link>
                </td>
              </tr>
              <tr v-if="distributions.length === 0">
                <td colspan="9" class="text-center py-4 text-muted">No distribution history available</td>
              </tr>
            </tbody>
          </o-table>
        </div>
      </div>

      <div class="container mt-3 mb-4" v-if="page.output">
        <o-pagination :page="meta.page" :total="meta.last_page || 0" @callback="navPage" />
      </div>
    </div>
    </FeatureGate>
  </div>
</template>

<script>
import store from '@/store/'
import { OTable, OPagination, OBreadCrumb } from '@/components/OmnihControl'
import FeatureGate from '@/components/upsell/FeatureGate.vue'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useToast } from '@/composables/useToast'

export default {
  components: { OTable, OPagination, OBreadCrumb, FeatureGate },

  setup() {
    useToast()
    const meta = computed(() => store.state.VoucherGift.meta)
    const distributions = computed(() => store.state.VoucherGift.distributions)

    const isVoucherGiftEnabled = computed(() => {
        const flag = store.getters['FeatureFlag/getVoucherGiftFlag'];
        return flag !== 'NONACTIVE';
    });

    const page = reactive({ isLoading: false, output: false })
    const validation = ref('')
    const filterStatus = ref('')
    const selfLoaded = ref(store.state.Auth.selfLoaded)
    const thisComponentShow = ref(true)

    // Search state
    const searchSelected = ref('voucher_name')
    const searchCriteria = ref('')
    const isSearchActive = ref(false)
    const searchOptions = reactive([
      { value: 'voucher_name', text: 'Voucher Name' },
    ])

    const fields = ref(['No', 'Voucher', 'Target', 'Total Sent', 'Status', 'Created By', 'Note', 'Created At'])

    watch(
      () => store.state.Auth.selfLoaded,
      (newVal) => { if (newVal) { selfLoaded.value = newVal; loadState() } }
    )

    onMounted(() => {
      store.commit('VoucherGift/reset_meta')
      store.commit('VoucherGift/clear_filter')
      store.commit('VoucherGift/clear_search')
      if (selfLoaded.value) loadState()
    })

    function loadState() {
      page.isLoading = true
      validation.value = ''

      const merchantId = store.state.Auth.selectedBusinessProfile?.merchant_id
      if (merchantId) {
        store.commit('VoucherGift/add_filter', { filter: { ...store.state.VoucherGift.filter, merchant_id: merchantId } })
      }

      store.dispatch('VoucherGift/getDistributionHistory')
        .catch((err) => { 
          let msg = err?.response?.data?.message || 'Failed to load history'
          if (typeof msg !== 'string') msg = JSON.stringify(msg)
          validation.value = msg
          thisComponentShow.value = false
        })
        .finally(() => { page.isLoading = false; page.output = true })
    }

    function doSearch() {
      if (!searchCriteria.value) {
        clearSearch()
        return
      }

      let search = {}
      search[searchSelected.value] = searchCriteria.value

      isSearchActive.value = true

      if (meta.value.page !== 1) {
        store.commit('VoucherGift/switch_page', { meta: { ...meta.value, page: 1 } })
      }

      store.commit('VoucherGift/set_search', { search })
      loadState()
    }

    function clearSearch() {
      isSearchActive.value = false
      searchCriteria.value = ''
      
      store.commit('VoucherGift/switch_page', { meta: { ...meta.value, page: 1 } })
      store.commit('VoucherGift/clear_search')
      loadState()
    }

    function navPage(p) {
      store.commit('VoucherGift/switch_page', { meta: { ...meta.value, page: p } })
      loadState()
    }

    function applyFilter() {
      const filter = {}
      if (filterStatus.value) {
        filter.status = filterStatus.value
      }
      store.commit('VoucherGift/add_filter', { filter })
      store.commit('VoucherGift/switch_page', { meta: { ...meta.value, page: 1 } })
      loadState()
    }

    function formatTarget(item) {
      if (item.target_type === 'all') return 'All Members'
      if (item.target_type === 'tier') return item.target_tier_name || 'Multiple Tiers'
      if (item.target_type === 'selected') return 'Selected Members'
      return item.target_type
    }

    function statusBadge(status) {
      const map = { 
        pending: 'status-pending', 
        processing: 'status-processing', 
        done: 'status-done', 
        failed: 'status-failed' 
      }
      return map[status] || 'status-inactive'
    }

    function formatDate(dateStr) {
      if (!dateStr) return '-'
      let dStr = dateStr
      if (typeof dateStr === 'string' && !dateStr.includes('T') && !dateStr.includes('Z')) {
        dStr = dateStr.replace(' ', 'T') + 'Z'
      }
      return new Date(dStr).toLocaleString('id-ID', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    }

    return { 
      page, fields, meta, distributions, validation, filterStatus, thisComponentShow,
      searchSelected, searchCriteria, isSearchActive, searchOptions,
      navPage, applyFilter, formatTarget, statusBadge, formatDate, doSearch, clearSearch, loadState,
      isVoucherGiftEnabled
    }
  }
}
</script>

<style scoped>
.filter-action-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    flex-wrap: wrap;
    background-color: #fff;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.filter-controls {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

.filter-section, .action-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.section-label-tabs {
    display: flex;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
}

.label-tab {
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-color, #0d6efd);
    background: white;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.search-box-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 1 350px;
    min-width: 250px;
}

.search-type-tabs {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
    flex-wrap: wrap;
}

.tab-btn {
    border: none;
    background: transparent;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    border-radius: 6px;
    transition: all 0.2s;
    cursor: pointer;
}

.tab-btn.active {
    background: white;
    color: var(--primary-color, #0d6efd);
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    font-weight: 600;
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    gap: 0;
    position: relative;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0;
    transition: all 0.2s;
    overflow: hidden;
}

.search-input-wrapper:focus-within {
    border-color: var(--primary-color, #0d6efd);
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
}

.search-input-wrapper input {
    border: none;
    padding: 10px 16px;
    font-size: 14px;
    width: 100%;
    outline: none;
    background: transparent;
    color: #334155;
    box-shadow: none;
}

.btn-search {
    background: #f8fafc;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;
}

.btn-search:hover {
    background: #e2e8f0;
    color: var(--primary-color, #0d6efd);
}

.search-input-wrapper .btn-search {
    position: relative;
    width: 44px;
    height: 42px;
    border-radius: 0;
    background: transparent;
}

.date-range-box {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 46px; 
}

.form-control-filter {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 14px;
    height: 46px; 
    min-width: 150px;
    background-color: white;
}

.form-control-filter:focus {
    border-color: var(--primary-color, #0d6efd);
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
}

.action-buttons-group {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.btn-refresh {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    border-radius: 8px;
}

.btn-add {
    background-color: var(--primary-color, #0d6efd);
    color: white;
    font-weight: 600;
    font-size: 13px;
    padding: 0 20px;
    height: 40px;
    display: flex;
    align-items: center;
    border: none;
    border-radius: 8px;
    transition: all 0.2s;
    text-decoration: none;
}

.member-list-table {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
}

.member-list-table td {
    padding: 12px 16px !important;
    vertical-align: middle;
    white-space: nowrap;
}

.member-list-table td .note-cell {
    white-space: normal;
    word-break: break-word;
    max-width: 220px;
    min-width: 120px;
    line-height: 1.5;
    font-size: 13px;
    color: #374151;
}

.member-row-clickable:hover {
    background-color: #f8fafc !important;
}

.date-text {
    font-size: 13px;
    color: #4a5568;
    white-space: nowrap;
}

.header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}

.header-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
    text-align: left;
}

.voucher-title {
    font-weight: 500;
    color: #1a1a2e;
}

.status-badge {
    padding: 0.35rem 0.85rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: capitalize;
    display: inline-block;
    text-align: center;
}

/* Spinner Overlay */
.spinner {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.7);
    z-index: 200;
}
/* Status Badges */
.status-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 6px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    display: inline-block;
}

.status-badge.status-pending {
    background-color: #fefce8;
    color: #854d0e;
}

.status-badge.status-processing {
    background-color: #ebf5ff;
    color: #004e9a;
}

.status-badge.status-done {
    background-color: #f0fdf4;
    color: #166534;
}

.status-badge.status-failed {
    background-color: #fff1f2;
    color: #9f1239;
}

.status-badge.status-inactive {
    background-color: #f1f5f9;
    color: #475569;
}

/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}

.btn-action {
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    border: none;
    cursor: pointer;
}

.btn-info-action {
    background-color: #eff6ff;
    color: #1e40af;
}

.btn-info-action:hover {
    background-color: #dbeafe;
    color: #1e3a8a;
}
</style>
